import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { tradePartnerAPI } from '../services/api';

export default function TradePartnerDashboard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tradePartnerId = searchParams.get('tradePartnerId');
  
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tradePartnerId) {
      setError('Trade Partner ID is required');
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Log API call for debugging
        console.log(`[TradePartnerDashboard] Fetching data for tradePartnerId: ${tradePartnerId}`);
        const startTime = Date.now();
        
        const response = await tradePartnerAPI.getDashboard(tradePartnerId);
        
        // Log successful response
        const duration = Date.now() - startTime;
        console.log(`[TradePartnerDashboard] API response received in ${duration}ms:`, response.data);
        
        // Validate response structure
        if (!response.data) {
          throw new Error('API returned empty response');
        }
        
        // Store data globally for debugging (remove in production)
        if (process.env.NODE_ENV === 'development') {
          window.__dashboardData = response.data;
        }
        
        setData(response.data);
      } catch (err) {
        // Enhanced error handling
        let errorMessage = 'Failed to load dashboard data';
        
        if (err.response) {
          // API responded with error status
          const status = err.response.status;
          if (status === 404) {
            errorMessage = `Trade partner not found (ID: ${tradePartnerId})`;
          } else if (status === 500) {
            errorMessage = 'Server error. Please try again later.';
          } else if (status >= 400 && status < 500) {
            errorMessage = err.response.data?.message || `Client error (${status})`;
          } else {
            errorMessage = `Server error (${status})`;
          }
        } else if (err.request) {
          // Request made but no response (timeout, network error)
          errorMessage = 'Network error. Please check if the backend is running on port 8081.';
        } else {
          // Something else happened
          errorMessage = err.message || errorMessage;
        }
        
        setError(errorMessage);
        console.error('[TradePartnerDashboard] Error fetching dashboard data:', {
          error: err,
          message: errorMessage,
          tradePartnerId,
          url: `http://localhost:8081/api/trade-partner/dashboard?tradePartnerId=${tradePartnerId}`
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [tradePartnerId]);

  const formatCurrency = (value) =>
    `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  // Score band styling
  const getScoreBandStyle = (band) => {
    const styles = {
      STRONG: { bg: 'rgba(34, 197, 94, 0.2)', text: '#4ade80', border: 'rgba(34, 197, 94, 0.3)' },
      STABLE: { bg: 'rgba(59, 130, 246, 0.2)', text: '#60a5fa', border: 'rgba(59, 130, 246, 0.3)' },
      WATCH: { bg: 'rgba(234, 179, 8, 0.2)', text: '#fbbf24', border: 'rgba(234, 179, 8, 0.3)' },
      AT_RISK: { bg: 'rgba(239, 68, 68, 0.2)', text: '#f87171', border: 'rgba(239, 68, 68, 0.3)' },
    };
    return styles[band] || styles.STABLE;
  };

  // Severity badge styling
  const getSeverityStyle = (severity) => {
    const styles = {
      LOW: { bg: 'rgba(34, 197, 94, 0.2)', text: '#4ade80', border: 'rgba(34, 197, 94, 0.3)' },
      MEDIUM: { bg: 'rgba(234, 179, 8, 0.2)', text: '#fbbf24', border: 'rgba(234, 179, 8, 0.3)' },
      HIGH: { bg: 'rgba(239, 68, 68, 0.2)', text: '#f87171', border: 'rgba(239, 68, 68, 0.3)' },
      CRITICAL: { bg: 'rgba(239, 68, 68, 0.2)', text: '#f87171', border: 'rgba(239, 68, 68, 0.3)' },
      MODERATE: { bg: 'rgba(234, 179, 8, 0.2)', text: '#fbbf24', border: 'rgba(234, 179, 8, 0.3)' },
    };
    return styles[severity] || styles.LOW;
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000000' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '3px solid rgba(139, 92, 246, 0.3)', 
            borderTop: '3px solid #8B5CF6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p style={{ color: '#999' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000000' }}>
        <div style={{ textAlign: 'center', padding: '1.5rem', backgroundColor: '#1A1A1A', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px' }}>
          <p style={{ color: '#f87171', fontWeight: '600', marginBottom: '0.5rem' }}>Error loading dashboard</p>
          <p style={{ color: '#fca5a5', fontSize: '0.875rem' }}>{error}</p>
          <button
            onClick={() => navigate('/cfo-dashboard')}
            style={{
              marginTop: '1rem',
              backgroundColor: '#8B5CF6',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Back to CFO Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Validate data structure and provide defaults
  const vitals = data.vitals || {};
  const constructScore = data.constructScore || { score: 0, band: 'STABLE' };
  const projects = Array.isArray(data.projects) ? data.projects : [];
  const capitalOpportunities = Array.isArray(data.capitalOpportunities) ? data.capitalOpportunities : [];
  const alerts = Array.isArray(data.alerts) ? data.alerts : [];
  const immediateActions = Array.isArray(data.immediateActions) ? data.immediateActions : [];
  const cashGap = data.cashGap || { amount: 0, severity: 'LOW' };

  // Log data structure for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('[TradePartnerDashboard] Rendering with data:', {
      vitals: Object.keys(vitals),
      constructScore: { score: constructScore.score, band: constructScore.band },
      projectsCount: projects.length,
      capitalOpportunitiesCount: capitalOpportunities.length,
      alertsCount: alerts.length,
      immediateActionsCount: immediateActions.length,
      cashGap: cashGap.amount
    });
  }

  // Get top 5 projects by risk (with safety checks)
  const topProjects = [...projects]
    .filter(p => p.riskIndicators?.overallRisk) // Filter out invalid entries
    .sort((a, b) => {
      const riskOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
      const aRisk = a.riskIndicators?.overallRisk || 'LOW';
      const bRisk = b.riskIndicators?.overallRisk || 'LOW';
      return riskOrder[bRisk] - riskOrder[aRisk];
    })
    .slice(0, 5);

  const bandStyle = getScoreBandStyle(constructScore.band);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#000000', padding: '1.5rem' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
              {data.tradePartnerName || 'CFO Insights'}
            </h1>
            <p style={{ fontSize: '0.875rem', color: '#999', marginTop: '0.25rem' }}>
              Trade Partner View · Cash, risk, and capital options in one view.
            </p>
          </div>
          <button
            onClick={() => navigate('/cfo-dashboard')}
            style={{
              backgroundColor: 'transparent',
              color: '#a78bfa',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              textDecoration: 'none'
            }}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Vitals Bar */}
        <div style={{ backgroundColor: '#1A1A1A', borderRadius: '8px', border: '1px solid #333', padding: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
            <VitalPill label="Cash" value={vitals.cashOnHand?.value || 0} formatCurrency={formatCurrency} subLabel="Cash on Hand" />
            <VitalPill label="Accounts Receivable" value={vitals.totalAR?.value || 0} formatCurrency={formatCurrency} subLabel="Outstanding" />
            <VitalPill label="Accounts Payable" value={vitals.totalAP?.value || 0} formatCurrency={formatCurrency} subLabel="Due" />
            <VitalPill label="Payroll" value={vitals.payrollDue?.value || 0} formatCurrency={formatCurrency} subLabel="Next Cycle" />
            <VitalPill label="Materials" value={vitals.materialsDue?.value || 0} formatCurrency={formatCurrency} subLabel="Upcoming" />
            <VitalPill 
              label="Jobs at Risk" 
              value={vitals.jobsAtRisk?.value || 0} 
              formatValue={(val) => val.toString()}
              subLabel={vitals.jobsAtRisk?.projectNames?.length ? `${vitals.jobsAtRisk.projectNames.length} projects` : undefined} 
            />
          </div>
        </div>

        {/* ConstructScore + Cash Gap */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
          {/* ConstructScore Card */}
          <div style={{ backgroundColor: '#1A1A1A', borderRadius: '8px', border: '1px solid #333', padding: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'white' }}>ConstructScore™</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ fontSize: '3rem', fontWeight: 'bold', color: 'white' }}>
                  {constructScore.score}/100
                </div>
                <span style={{
                  padding: '0.375rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  backgroundColor: bandStyle.bg,
                  color: bandStyle.text,
                  border: `1px solid ${bandStyle.border}`
                }}>
                  {constructScore.band}
                </span>
              </div>
              {constructScore.bandSubtitle && (
                <p style={{ fontSize: '0.875rem', color: '#d1d5db', fontStyle: 'italic' }}>
                  {constructScore.bandSubtitle}
                </p>
              )}
              {constructScore.strengths && constructScore.strengths.length > 0 && (
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#d1d5db', marginBottom: '0.5rem' }}>Key strengths:</p>
                  <ul style={{ listStyle: 'disc', listStylePosition: 'inside', fontSize: '0.875rem', color: '#9ca3af', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {constructScore.strengths.slice(0, 3).map((strength, idx) => (
                      <li key={idx}>{strength}</li>
                    ))}
                  </ul>
                </div>
              )}
              {constructScore.improvements && constructScore.improvements.length > 0 && (
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: '600', color: '#d1d5db', marginBottom: '0.5rem' }}>Areas to improve:</p>
                  <ul style={{ listStyle: 'disc', listStylePosition: 'inside', fontSize: '0.875rem', color: '#9ca3af', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {constructScore.improvements.slice(0, 2).map((improvement, idx) => (
                      <li key={idx}>{improvement}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Cash Gap Panel */}
          <div style={{ backgroundColor: '#1A1A1A', borderRadius: '8px', border: '1px solid #333', padding: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'white' }}>Cash Gap</h2>
            {cashGap && cashGap.amount > 0 ? (
              <div style={{
                padding: '1rem',
                borderRadius: '8px',
                border: `2px solid ${
                  cashGap.severity === 'CRITICAL' || cashGap.severity === 'HIGH'
                    ? 'rgba(239, 68, 68, 0.3)'
                    : cashGap.severity === 'MODERATE'
                    ? 'rgba(234, 179, 8, 0.3)'
                    : 'rgba(34, 197, 94, 0.3)'
                }`,
                backgroundColor:
                  cashGap.severity === 'CRITICAL' || cashGap.severity === 'HIGH'
                    ? 'rgba(239, 68, 68, 0.1)'
                    : cashGap.severity === 'MODERATE'
                    ? 'rgba(234, 179, 8, 0.1)'
                    : 'rgba(34, 197, 94, 0.1)'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '0.25rem' }}>Estimated Cash Gap</p>
                <p style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color:
                    cashGap.severity === 'CRITICAL' || cashGap.severity === 'HIGH'
                      ? '#f87171'
                      : cashGap.severity === 'MODERATE'
                      ? '#fbbf24'
                      : '#4ade80'
                }}>
                  {formatCurrency(cashGap.amount)}
                </p>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>over the next 30 days</p>
              </div>
            ) : (
              <div style={{ padding: '1rem', borderRadius: '8px', backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '2px solid rgba(34, 197, 94, 0.3)' }}>
                <p style={{ fontSize: '1rem', color: '#4ade80', fontWeight: '500' }}>
                  No immediate cash gap detected.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Capital Opportunities */}
        <div style={{ backgroundColor: '#1A1A1A', borderRadius: '8px', border: '1px solid #333', padding: '1.5rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'white' }}>
            Recommended Capital Options
          </h2>
          {capitalOpportunities.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
              {capitalOpportunities.map((offer) => (
                <CapitalOfferCard
                  key={offer.id}
                  offer={offer}
                  constructScoreBand={constructScore.band}
                  formatCurrency={formatCurrency}
                  formatPercent={formatPercent}
                  bandStyle={getScoreBandStyle(constructScore.band)}
                />
              ))}
            </div>
          ) : (
            <div style={{ padding: '1.5rem', backgroundColor: 'rgba(55, 65, 81, 0.5)', borderRadius: '8px', border: '1px solid #4b5563' }}>
              <p style={{ fontSize: '0.875rem', color: '#9ca3af', textAlign: 'center' }}>
                No capital options are currently available based on your score and cash gap.
                Improving cash buffer and payment speed can unlock options.
              </p>
            </div>
          )}
        </div>

        {/* Projects Table */}
        {topProjects.length > 0 && (
          <div style={{ backgroundColor: '#1A1A1A', borderRadius: '8px', border: '1px solid #333', padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'white' }}>Projects</h2>
              {projects.length > 5 && (
                <button style={{ fontSize: '0.875rem', color: '#a78bfa', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>
                  View all projects ({projects.length})
                </button>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <th style={{ textAlign: 'left', padding: '0.5rem 1rem', fontWeight: '600', color: '#d1d5db' }}>Project Name</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem 1rem', fontWeight: '600', color: '#d1d5db' }}>GC Name</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem 1rem', fontWeight: '600', color: '#d1d5db' }}>Contract Value</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem 1rem', fontWeight: '600', color: '#d1d5db' }}>Risk Indicator</th>
                  </tr>
                </thead>
                <tbody>
                  {topProjects.map((project) => {
                    const overallRisk = project.riskIndicators?.overallRisk || 'LOW';
                    const riskStyle = overallRisk === 'HIGH'
                      ? { bg: 'rgba(239, 68, 68, 0.2)', text: '#f87171', border: 'rgba(239, 68, 68, 0.3)' }
                      : overallRisk === 'MEDIUM'
                      ? { bg: 'rgba(234, 179, 8, 0.2)', text: '#fbbf24', border: 'rgba(234, 179, 8, 0.3)' }
                      : { bg: 'rgba(34, 197, 94, 0.2)', text: '#4ade80', border: 'rgba(34, 197, 94, 0.3)' };
                    return (
                      <tr key={project.id} style={{ borderBottom: '1px solid #374151' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '500', color: 'white' }}>{project.name || 'Unnamed Project'}</td>
                        <td style={{ padding: '0.75rem 1rem', color: '#9ca3af' }}>{project.gcName || 'N/A'}</td>
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '500', color: 'white' }}>
                          {formatCurrency(project.contractValue || 0)}
                        </td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            backgroundColor: riskStyle.bg,
                            color: riskStyle.text,
                            border: `1px solid ${riskStyle.border}`
                          }}>
                            {overallRisk}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Alerts & Immediate Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
          {/* Alerts */}
          {alerts.length > 0 && (
            <div style={{ backgroundColor: '#1A1A1A', borderRadius: '8px', border: '1px solid #333', padding: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'white' }}>Alerts</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {alerts.map((alert) => {
                  const severityStyle = getSeverityStyle(alert.severity);
                  return (
                    <div
                      key={alert.id}
                      style={{
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: `1px solid ${severityStyle.border}`,
                        backgroundColor: severityStyle.bg
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '0.5rem' }}>
                        <div style={{ flex: 1 }}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: severityStyle.bg,
                            color: severityStyle.text,
                            border: `1px solid ${severityStyle.border}`,
                            marginRight: '0.5rem'
                          }}>
                            {alert.severity}
                          </span>
                          <p style={{ fontSize: '0.875rem', fontWeight: '500', marginTop: '0.25rem', color: '#e5e7eb' }}>
                            {alert.message}
                          </p>
                        </div>
                      </div>
                      {alert.actionUrl && (
                        <a
                          href={alert.actionUrl}
                          style={{
                            fontSize: '0.75rem',
                            color: '#a78bfa',
                            textDecoration: 'none',
                            marginTop: '0.5rem',
                            display: 'inline-block'
                          }}
                        >
                          Take action →
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Immediate Actions */}
          {immediateActions.length > 0 && (
            <div style={{ backgroundColor: '#1A1A1A', borderRadius: '8px', border: '1px solid #333', padding: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'white' }}>
                Recommended Next Steps
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {immediateActions.map((action) => (
                  <div
                    key={action.id}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #374151',
                      borderRadius: '8px'
                    }}
                  >
                    <p style={{ fontSize: '0.875rem', color: '#e5e7eb' }}>
                      {action.title}
                    </p>
                    {action.amount > 0 && (
                      <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                        Amount: {formatCurrency(action.amount)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Helper Components
function VitalPill({ label, value, formatCurrency, formatValue, subLabel }) {
  const displayValue = formatValue ? formatValue(value) : (formatCurrency ? formatCurrency(value) : value.toString());
  
  return (
    <div style={{ backgroundColor: 'rgba(55, 65, 81, 0.5)', borderRadius: '8px', padding: '0.75rem', border: '1px solid #4b5563' }}>
      <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.25rem' }}>{label}</p>
      <p style={{ fontSize: '1rem', fontWeight: '600', color: 'white' }}>
        {displayValue}
      </p>
      {subLabel && (
        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>{subLabel}</p>
      )}
    </div>
  );
}

function CapitalOfferCard({ offer, constructScoreBand, formatCurrency, formatPercent, bandStyle }) {
  const offerTypeLabels = {
    PAYROLL_BRIDGE: 'Payroll Bridge',
    MATERIAL_FINANCING: 'Materials Financing',
    EARLY_PAY: 'Early Pay Available',
    VIRTUAL_CARD: 'Virtual Card',
    GC_BACKED_FUND: 'GC-Backed Fund',
  };

  const offerDescriptions = {
    PAYROLL_BRIDGE: 'Bridge payroll for 1–2 cycles.',
    MATERIAL_FINANCING: 'Finance materials for upcoming projects.',
    EARLY_PAY: 'Get paid early on approved invoices.',
    VIRTUAL_CARD: 'Virtual card for supplier payments.',
    GC_BACKED_FUND: 'GC-backed working capital fund.',
  };

  return (
    <div style={{
      border: '1px solid #374151',
      borderRadius: '8px',
      padding: '1rem',
      backgroundColor: 'rgba(55, 65, 81, 0.3)'
    }}>
      <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h3 style={{ fontWeight: '600', fontSize: '1rem', color: 'white' }}>
          {offer.title || offerTypeLabels[offer.offerType]}
        </h3>
        <span style={{
          padding: '0.25rem 0.5rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: '500',
          backgroundColor: bandStyle.bg,
          color: bandStyle.text,
          border: `1px solid ${bandStyle.border}`
        }}>
          {constructScoreBand}
        </span>
      </div>
      <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '0.75rem' }}>
        {offer.description || offerDescriptions[offer.offerType]}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
          <span style={{ color: '#9ca3af' }}>Limit:</span>
          <span style={{ fontWeight: '600', color: 'white' }}>Up to {formatCurrency(offer.maxAmount)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
          <span style={{ color: '#9ca3af' }}>APR:</span>
          <span style={{ fontWeight: '600', color: 'white' }}>
            {offer.discountRate
              ? formatPercent(offer.discountRate * 100)
              : formatPercent(offer.pricingAPR)}
          </span>
        </div>
      </div>
      <button style={{
        width: '100%',
        backgroundColor: '#8B5CF6',
        color: 'white',
        padding: '0.5rem 1rem',
        borderRadius: '8px',
        border: 'none',
        fontSize: '0.875rem',
        fontWeight: '500',
        cursor: 'pointer'
      }}>
        {offer.callToAction || 'Get Started'}
      </button>
    </div>
  );
}

